import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import { clearFederatedBootstrap } from './federated-bootstrap';
import type {
  HouseholdSharingPort,
  HouseholdSharingStatus,
  HouseholdSyncResult,
  SharedHouseholdDevice,
  SharedHouseholdSummary,
} from '../application/household-sharing';
import { FROZEN_SAVE_HOUSEHOLD_STREAM_ID } from '../application/household-sharing';
import type { InventorySnapshot } from '../domain/model';
import { createBackup, parseBackup } from './backup';

const API_PREFIX = '/api/household';
const PUBLIC_APP_ORIGIN = 'https://app.frozensave.com';
const INVITATION_STORAGE_KEY = 'frozen-save.household-invitation-v1';
const SNAPSHOT_CONTRACT = 'frozen-save.household-snapshot.v1';
const KEY_ENVELOPE_CONTRACT = 'frozen-save.household-key-envelope.v1';
const MAX_JOURNAL_PAGES = 20;
const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

interface DeviceRecord {
  readonly key: string;
  readonly keyId: string;
  readonly privateKey: CryptoKey;
  readonly publicJwk: JsonWebKey;
  readonly createdAt: string;
}

interface BindingRecord {
  readonly householdId: string;
  readonly accountId: string;
  readonly localHouseholdId: string | null;
  readonly role: 'member' | 'owner';
  readonly keyVersion: number;
  readonly householdKey: CryptoKey | null;
  readonly checkpoint: number;
  readonly lastSyncedDigest: string | null;
  readonly updatedAt: string;
}

interface SharingDatabase extends DBSchema {
  device: { key: string; value: DeviceRecord };
  bindings: {
    key: string;
    value: BindingRecord;
    indexes: { 'by-local-household': string };
  };
}

interface ApiErrorBody { readonly code?: unknown }

export class HouseholdSharingApiError extends Error {
  constructor(readonly code: string, readonly status: number) {
    super(code);
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const base64Url = (value: Uint8Array): string => {
  let binary = '';
  for (const byte of value) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/u, '');
};

const fromBase64Url = (value: unknown): Uint8Array => {
  if (typeof value !== 'string' || !/^[A-Za-z0-9_-]+$/u.test(value)) {
    throw new Error('HOUSEHOLD_BASE64_INVALID');
  }
  const padded = `${value.replaceAll('-', '+').replaceAll('_', '/')}${'='.repeat((4 - value.length % 4) % 4)}`;
  return Uint8Array.from(atob(padded), (character) => character.charCodeAt(0));
};

const arrayBuffer = (value: Uint8Array): ArrayBuffer => Uint8Array.from(value).buffer;

const sha256 = async (value: string): Promise<string> => {
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', textEncoder.encode(value)));
  return base64Url(digest);
};

const snapshotDigest = async (snapshot: InventorySnapshot): Promise<string> => {
  const candidate = structuredClone(snapshot);
  candidate.meta = {
    ...candidate.meta,
    deviceId: 'shared-device',
    deviceSequence: 0,
    persistentStorage: 'unknown',
    updatedAt: candidate.meta.createdAt,
  };
  return sha256(JSON.stringify(candidate));
};

const headerValue = (headers: Readonly<Record<string, string>>, name: string): string | null => {
  const entry = Object.entries(headers).find(([key]) => key.toLowerCase() === name.toLowerCase());
  return entry?.[1] ?? null;
};

export const householdApiRequest = async <T>(
  path: string,
  init: { readonly body?: unknown; readonly method?: 'DELETE' | 'GET' | 'POST' } = {},
): Promise<T> => {
  if (Capacitor.isNativePlatform()) {
    const response = await CapacitorHttp.request({
      url: `${PUBLIC_APP_ORIGIN}${API_PREFIX}${path}`,
      method: init.method ?? 'POST',
      headers: {
        Accept: 'application/json',
        Origin: PUBLIC_APP_ORIGIN,
        'X-Frozen-Save-Request': 'handoff-v1',
        ...(init.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      ...(init.body === undefined ? {} : { data: init.body }),
      connectTimeout: 15_000,
      readTimeout: 15_000,
      disableRedirects: true,
    });
    const value = typeof response.data === 'string'
      ? (() => {
          try {
            return JSON.parse(response.data) as ApiErrorBody;
          } catch {
            return { code: 'HOUSEHOLD_RESPONSE_INVALID' };
          }
        })()
      : response.data as ApiErrorBody;
    if (!headerValue(response.headers, 'cache-control')
      ?.split(',').some((part) => part.trim() === 'no-store')) {
      throw new HouseholdSharingApiError('HOUSEHOLD_CACHE_POLICY_INVALID', 502);
    }
    if (response.status < 200 || response.status >= 300) {
      throw new HouseholdSharingApiError(
        typeof value?.code === 'string' ? value.code : 'HOUSEHOLD_REQUEST_FAILED',
        response.status,
      );
    }
    return value as T;
  }

  const response = await fetch(`${API_PREFIX}${path}`, {
    cache: 'no-store',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'X-Frozen-Save-Request': 'handoff-v1',
      ...(init.body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    method: init.method ?? 'POST',
    redirect: 'error',
    ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
  });
  const value = await response.json().catch(() => ({ code: 'HOUSEHOLD_RESPONSE_INVALID' })) as ApiErrorBody;
  if (!response.headers.get('cache-control')?.split(',').some((part) => part.trim() === 'no-store')) {
    throw new HouseholdSharingApiError('HOUSEHOLD_CACHE_POLICY_INVALID', 502);
  }
  if (!response.ok) {
    throw new HouseholdSharingApiError(
      typeof value.code === 'string' ? value.code : 'HOUSEHOLD_REQUEST_FAILED',
      response.status,
    );
  }
  return value as T;
};

const request = householdApiRequest;

const safeInvitationToken = (value: string | null): string | null =>
  value != null && /^[A-Za-z0-9._~-]{20,1024}$/u.test(value) ? value : null;

const takeInvitationToken = (): string | null => {
  const url = new URL(window.location.href);
  const hash = new URLSearchParams(url.hash.replace(/^#/u, ''));
  const fromUrl = safeInvitationToken(hash.get('household_invite'));
  if (hash.has('household_invite')) {
    hash.delete('household_invite');
    url.hash = hash.toString();
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  }
  if (fromUrl != null) sessionStorage.setItem(INVITATION_STORAGE_KEY, fromUrl);
  return fromUrl ?? safeInvitationToken(sessionStorage.getItem(INVITATION_STORAGE_KEY));
};

const parseHouseholds = (value: unknown): readonly SharedHouseholdSummary[] => {
  if (!isRecord(value) || !Array.isArray(value.households)) throw new Error('HOUSEHOLD_STATUS_INVALID');
  return value.households.map((entry) => {
    if (
      !isRecord(entry) || typeof entry.householdId !== 'string' ||
      typeof entry.joinedAt !== 'string' || !Number.isFinite(Date.parse(entry.joinedAt)) ||
      !Number.isInteger(entry.memberCount) || Number(entry.memberCount) < 1 || Number(entry.memberCount) > 5 ||
      (entry.role !== 'owner' && entry.role !== 'member')
    ) throw new Error('HOUSEHOLD_STATUS_INVALID');
    return {
      householdId: entry.householdId,
      joinedAt: entry.joinedAt,
      memberCount: Number(entry.memberCount),
      role: entry.role,
    };
  });
};

const keyContext = (householdId: string, keyVersion: number, recipientKeyId: string): string =>
  `${KEY_ENVELOPE_CONTRACT}\u0000${householdId}\u0000${keyVersion}\u0000${recipientKeyId}`;

const snapshotContext = (
  householdId: string,
  keyVersion: number,
  operationId: string,
): string => `${SNAPSHOT_CONTRACT}\u0000${householdId}\u0000${FROZEN_SAVE_HOUSEHOLD_STREAM_ID}\u0000${keyVersion}\u0000${operationId}`;

const generateDevice = async (nativeKeyId?: string): Promise<DeviceRecord> => {
  const generated = await crypto.subtle.generateKey(
    { name: 'ECDH', namedCurve: 'P-256' },
    true,
    ['deriveBits'],
  );
  const publicJwk = await crypto.subtle.exportKey('jwk', generated.publicKey);
  const privateJwk = await crypto.subtle.exportKey('jwk', generated.privateKey);
  const privateKey = await crypto.subtle.importKey(
    'jwk',
    privateJwk,
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    ['deriveBits'],
  );
  if (
    publicJwk.kty !== 'EC' || publicJwk.crv !== 'P-256' ||
    typeof publicJwk.x !== 'string' || typeof publicJwk.y !== 'string'
  ) throw new Error('HOUSEHOLD_DEVICE_KEY_INVALID');
  return {
    key: nativeKeyId ?? 'current',
    keyId: nativeKeyId ?? `fs-device-${crypto.randomUUID()}`,
    privateKey,
    publicJwk: { crv: 'P-256', kty: 'EC', x: publicJwk.x, y: publicJwk.y },
    createdAt: new Date().toISOString(),
  };
};

const deriveWrappingKey = async (
  privateKey: CryptoKey,
  publicJwk: JsonWebKey,
  context: string,
): Promise<CryptoKey> => {
  const publicKey = await crypto.subtle.importKey(
    'jwk',
    publicJwk,
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    [],
  );
  const shared = new Uint8Array(await crypto.subtle.deriveBits(
    { name: 'ECDH', public: publicKey },
    privateKey,
    256,
  ));
  try {
    const material = await crypto.subtle.importKey('raw', shared, 'HKDF', false, ['deriveKey']);
    return await crypto.subtle.deriveKey(
      { hash: 'SHA-256', info: textEncoder.encode(context), name: 'HKDF', salt: new Uint8Array(32) },
      material,
      { length: 256, name: 'AES-GCM' },
      false,
      ['decrypt', 'encrypt'],
    );
  } finally {
    shared.fill(0);
  }
};

const wrapHouseholdKey = async (
  householdKey: CryptoKey,
  householdId: string,
  keyVersion: number,
  recipientKeyId: string,
  recipientPublicJwk: JsonWebKey,
) => {
  const ephemeral = await crypto.subtle.generateKey(
    { name: 'ECDH', namedCurve: 'P-256' },
    true,
    ['deriveBits'],
  );
  const wrappingKey = await deriveWrappingKey(
    ephemeral.privateKey,
    recipientPublicJwk,
    keyContext(householdId, keyVersion, recipientKeyId),
  );
  const raw = new Uint8Array(await crypto.subtle.exportKey('raw', householdKey));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  try {
    const ciphertext = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, wrappingKey, raw));
    const ephemeralPublic = await crypto.subtle.exportKey('jwk', ephemeral.publicKey);
    return {
      contractVersion: KEY_ENVELOPE_CONTRACT,
      alg: 'ECDH-ES+A256GCM',
      ciphertext: base64Url(ciphertext),
      iv: base64Url(iv),
      ephemeralPublicJwk: {
        crv: 'P-256',
        kty: 'EC',
        x: ephemeralPublic.x,
        y: ephemeralPublic.y,
      },
    };
  } finally {
    raw.fill(0);
  }
};

const unwrapHouseholdKey = async (
  envelope: unknown,
  device: DeviceRecord,
  householdId: string,
  keyVersion: number,
): Promise<CryptoKey> => {
  if (!isRecord(envelope) || envelope.contractVersion !== KEY_ENVELOPE_CONTRACT ||
      envelope.alg !== 'ECDH-ES+A256GCM' || !isRecord(envelope.ephemeralPublicJwk)) {
    throw new Error('HOUSEHOLD_KEY_ENVELOPE_INVALID');
  }
  const wrappingKey = await deriveWrappingKey(
    device.privateKey,
    envelope.ephemeralPublicJwk,
    keyContext(householdId, keyVersion, device.keyId),
  );
  const raw = new Uint8Array(await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: arrayBuffer(fromBase64Url(envelope.iv)) },
    wrappingKey,
    arrayBuffer(fromBase64Url(envelope.ciphertext)),
  ));
  try {
    if (raw.byteLength !== 32) throw new Error('HOUSEHOLD_KEY_INVALID');
    return await crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, true, ['decrypt', 'encrypt']);
  } finally {
    raw.fill(0);
  }
};

const encryptSnapshot = async (
  householdKey: CryptoKey,
  householdId: string,
  keyVersion: number,
  operationId: string,
  snapshot: InventorySnapshot,
) => {
  const backup = await createBackup(snapshot, __APP_VERSION__);
  const plaintext = textEncoder.encode(JSON.stringify(backup));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  try {
    const ciphertext = new Uint8Array(await crypto.subtle.encrypt(
      {
        additionalData: textEncoder.encode(snapshotContext(householdId, keyVersion, operationId)),
        iv,
        name: 'AES-GCM',
      },
      householdKey,
      plaintext,
    ));
    return {
      contractVersion: SNAPSHOT_CONTRACT,
      alg: 'A256GCM',
      ciphertext: base64Url(ciphertext),
      iv: base64Url(iv),
    };
  } finally {
    plaintext.fill(0);
  }
};

const decryptSnapshot = async (
  envelope: unknown,
  householdKey: CryptoKey,
  householdId: string,
  keyVersion: number,
  operationId: string,
): Promise<InventorySnapshot> => {
  if (!isRecord(envelope) || envelope.contractVersion !== SNAPSHOT_CONTRACT || envelope.alg !== 'A256GCM') {
    throw new Error('HOUSEHOLD_SNAPSHOT_ENVELOPE_INVALID');
  }
  const plaintext = new Uint8Array(await crypto.subtle.decrypt(
    {
      additionalData: textEncoder.encode(snapshotContext(householdId, keyVersion, operationId)),
      iv: arrayBuffer(fromBase64Url(envelope.iv)),
      name: 'AES-GCM',
    },
    householdKey,
    arrayBuffer(fromBase64Url(envelope.ciphertext)),
  ));
  try {
    const backup = await parseBackup(textDecoder.decode(plaintext));
    return backup.data;
  } finally {
    plaintext.fill(0);
  }
};

const disconnected = (error: unknown): boolean =>
  error instanceof HouseholdSharingApiError && error.status === 401;

export class ProductionHouseholdSharing implements HouseholdSharingPort {
  readonly available = true;
  private readonly databasePromise: Promise<IDBPDatabase<SharingDatabase>>;
  private activeAccountId: string | null = null;
  private activeDeviceKeyId: string | null = null;
  private invitationToken = takeInvitationToken();

  constructor(databaseName: string) {
    this.databasePromise = openDB<SharingDatabase>(`${databaseName}-household-sharing`, 2, {
      upgrade(database) {
        if (database.objectStoreNames.contains('device')) return;
        database.createObjectStore('device', { keyPath: 'key' });
        const bindings = database.createObjectStore('bindings', { keyPath: 'householdId' });
        bindings.createIndex('by-local-household', 'localHouseholdId', { unique: true });
      },
    });
  }

  async status(localHouseholdId: string | null): Promise<HouseholdSharingStatus> {
    try {
      const value = await request<unknown>('/session/status');
      if (!isRecord(value)) throw new Error('HOUSEHOLD_STATUS_INVALID');
      if (value.state === 'disconnected') {
        this.activeAccountId = null;
        this.activeDeviceKeyId = null;
        return { state: 'disconnected' };
      }
      if (
        value.state !== 'connected' || typeof value.productSubjectId !== 'string' ||
        !/^fsa_[A-Za-z0-9_-]{24,80}$/u.test(value.productSubjectId)
      ) throw new Error('HOUSEHOLD_STATUS_INVALID');
      this.activeAccountId = value.productSubjectId;
      if (value.deviceKeyId != null && (typeof value.deviceKeyId !== 'string' ||
        !/^fs-device-[0-9a-f-]{36}$/u.test(value.deviceKeyId))) throw new Error('HOUSEHOLD_STATUS_INVALID');
      this.activeDeviceKeyId = typeof value.deviceKeyId === 'string' ? value.deviceKeyId : null;
      const households = parseHouseholds(value);
      const allBindings = await (await this.databasePromise).getAll('bindings');
      const accountBindings = allBindings.filter((binding) => binding.accountId === value.productSubjectId);
      const localBinding = await this.bindingForLocal(localHouseholdId);
      const binding = localBinding?.accountId === value.productSubjectId
        ? localBinding
        : accountBindings.find((candidate) =>
          households.some((household) => household.householdId === candidate.householdId)) ?? null;
      if (binding != null && !households.some((household) => household.householdId === binding.householdId)) {
        await this.saveBinding({ ...binding, householdKey: null, updatedAt: new Date().toISOString() });
        return { state: 'revoked' };
      }
      const ready = binding?.householdKey == null && binding != null
        ? await this.claimHouseholdKey(binding).catch(() => binding)
        : binding;
      return {
        state: 'connected',
        productSubjectId: value.productSubjectId,
        households,
        binding: ready == null ? null : {
          householdId: ready.householdId,
          keyReady: ready.householdKey != null,
          keyVersion: ready.keyVersion,
          role: ready.role,
        },
      };
    } catch (error) {
      return disconnected(error) ? { state: 'disconnected' } : { state: 'unavailable' };
    }
  }

  async signup(handle: string, email: string, passphrase: string, passphraseConfirmation: string) {
    const value = await request<unknown>('/account/signup', {
      body: { deviceId: (await this.device()).keyId, email, handle, passphrase, passphraseConfirmation },
    });
    if (!isRecord(value) || typeof value.recoveryCode !== 'string' ||
        !/^fsr_[A-Za-z0-9_-]{32}$/u.test(value.recoveryCode)) {
      throw new Error('HOUSEHOLD_ACCOUNT_RESPONSE_INVALID');
    }
    return { recoveryCode: value.recoveryCode, status: await this.status(null) };
  }

  async login(handle: string, passphrase: string): Promise<HouseholdSharingStatus> {
    await request('/account/login', {
      body: { deviceId: (await this.device()).keyId, handle, passphrase },
    });
    return this.bindAuthenticatedHousehold(await this.status(null));
  }

  restoreFederatedSession(): Promise<HouseholdSharingStatus> {
    return this.status(null).then((status) => this.bindAuthenticatedHousehold(status));
  }

  async recover(
    handle: string,
    recoveryCode: string,
    passphrase: string,
    passphraseConfirmation: string,
  ) {
    const value = await request<unknown>('/account/recover', {
      body: { deviceId: (await this.device()).keyId, handle, passphrase, passphraseConfirmation, recoveryCode },
    });
    if (!isRecord(value) || typeof value.recoveryCode !== 'string' ||
        !/^fsr_[A-Za-z0-9_-]{32}$/u.test(value.recoveryCode)) {
      throw new Error('HOUSEHOLD_ACCOUNT_RESPONSE_INVALID');
    }
    return {
      recoveryCode: value.recoveryCode,
      status: await this.bindAuthenticatedHousehold(await this.status(null)),
    };
  }

  async deleteAccount(passphrase: string): Promise<void> {
    await request('/account/delete', { body: { passphrase } });
    const database = await this.databasePromise;
    await database.clear('bindings');
    this.activeAccountId = null;
    this.activeDeviceKeyId = null;
  }

  async disconnect(): Promise<void> {
    await request('/session', { method: 'DELETE' });
    this.activeAccountId = null;
    this.activeDeviceKeyId = null;
    clearFederatedBootstrap();
  }

  async create(snapshot: InventorySnapshot): Promise<HouseholdSharingStatus> {
    if (snapshot.household == null) throw new Error('HOUSEHOLD_LOCAL_REQUIRED');
    const device = await this.device();
    await this.registerDevice(device);
    const created = await request<unknown>('/create');
    if (!isRecord(created) || typeof created.householdId !== 'string') {
      throw new Error('HOUSEHOLD_CREATE_RESPONSE_INVALID');
    }
    const householdKey = await crypto.subtle.generateKey(
      { length: 256, name: 'AES-GCM' },
      true,
      ['decrypt', 'encrypt'],
    );
    const binding: BindingRecord = {
      householdId: created.householdId,
      accountId: String(created.ownerProductSubjectId),
      localHouseholdId: snapshot.household.id,
      role: 'owner',
      keyVersion: 1,
      householdKey,
      checkpoint: 0,
      lastSyncedDigest: null,
      updatedAt: new Date().toISOString(),
    };
    await this.saveBinding(binding);
    await this.putEnvelope(binding, device.keyId, String(created.ownerProductSubjectId), device.publicJwk);
    await this.synchronize(snapshot);
    return this.status(snapshot.household.id);
  }

  async join(invitationToken = this.invitationToken ?? undefined): Promise<HouseholdSharingStatus> {
    const token = safeInvitationToken(invitationToken ?? null);
    if (token == null) throw new Error('HOUSEHOLD_INVITATION_REQUIRED');
    const device = await this.device();
    await this.registerDevice(device);
    const joined = await request<unknown>('/join', { body: { invitationToken: token } });
    if (!isRecord(joined) || typeof joined.householdId !== 'string' || joined.role !== 'member') {
      throw new Error('HOUSEHOLD_JOIN_RESPONSE_INVALID');
    }
    await this.saveBinding({
      householdId: joined.householdId,
      accountId: String(joined.productSubjectId),
      localHouseholdId: null,
      role: 'member',
      keyVersion: 1,
      householdKey: null,
      checkpoint: 0,
      lastSyncedDigest: null,
      updatedAt: new Date().toISOString(),
    });
    sessionStorage.removeItem(INVITATION_STORAGE_KEY);
    this.invitationToken = null;
    await this.claimHouseholdKey((await this.binding(joined.householdId))!).catch(() => undefined);
    return this.status(null);
  }

  async invitationLink(householdId: string): Promise<{ readonly expiresAt: string; readonly url: string }> {
    const value = await request<unknown>('/invitation', {
      body: { householdId, requestId: `fsInvite_${crypto.randomUUID()}` },
    });
    if (!isRecord(value) || typeof value.invitationToken !== 'string' ||
        safeInvitationToken(value.invitationToken) == null || typeof value.expiresAt !== 'string') {
      throw new Error('HOUSEHOLD_INVITATION_RESPONSE_INVALID');
    }
    const url = new URL('/', Capacitor.isNativePlatform() ? PUBLIC_APP_ORIGIN : window.location.origin);
    url.hash = new URLSearchParams({ household_invite: value.invitationToken }).toString();
    return { expiresAt: value.expiresAt, url: url.toString() };
  }

  async devices(householdId: string): Promise<readonly SharedHouseholdDevice[]> {
    const [value, current, sessionValue] = await Promise.all([
      request<unknown>('/devices', { body: { householdId } }),
      this.device(),
      request<unknown>('/session/status'),
    ]);
    if (!isRecord(value) || !Array.isArray(value.devices) || !isRecord(sessionValue) ||
        typeof sessionValue.productSubjectId !== 'string') throw new Error('HOUSEHOLD_DEVICES_INVALID');
    return value.devices.map((entry) => {
      if (!isRecord(entry) || typeof entry.keyId !== 'string' ||
          typeof entry.productSubjectId !== 'string' || (entry.role !== 'owner' && entry.role !== 'member')) {
        throw new Error('HOUSEHOLD_DEVICES_INVALID');
      }
      return {
        keyId: entry.keyId,
        productSubjectId: entry.productSubjectId,
        role: entry.role,
        current: entry.keyId === current.keyId && entry.productSubjectId === sessionValue.productSubjectId,
      };
    });
  }

  async authorizeDevices(householdId: string): Promise<number> {
    const binding = await this.requiredBinding(householdId);
    if (binding.role !== 'owner' || binding.householdKey == null) throw new Error('HOUSEHOLD_OWNER_REQUIRED');
    const value = await request<unknown>('/devices', { body: { householdId } });
    if (!isRecord(value) || !Array.isArray(value.devices)) throw new Error('HOUSEHOLD_DEVICES_INVALID');
    let count = 0;
    for (const entry of value.devices) {
      if (!isRecord(entry) || typeof entry.keyId !== 'string' ||
          typeof entry.productSubjectId !== 'string' || !isRecord(entry.publicJwk)) {
        throw new Error('HOUSEHOLD_DEVICES_INVALID');
      }
      await this.putEnvelope(binding, entry.keyId, entry.productSubjectId, entry.publicJwk);
      count += 1;
    }
    return count;
  }

  async synchronize(snapshot: InventorySnapshot, preferRemote = false): Promise<HouseholdSyncResult> {
    const session = await this.status(snapshot.household?.id ?? null);
    if (session.state === 'revoked') return { state: 'revoked' };
    if (session.state !== 'connected') return { state: 'unavailable' };
    const localBinding = await this.bindingForLocal(snapshot.household?.id ?? null);
    // Un stock local associé à A ne peut jamais être envoyé avec la nouvelle session B.
    if (localBinding != null && localBinding.accountId !== session.productSubjectId) {
      return { state: 'unavailable' };
    }
    const found = localBinding ?? await this.firstBinding();
    if (found == null) return { state: 'unavailable' };
    let binding: BindingRecord = found;
    if (binding.householdKey == null) {
      try {
        binding = await this.claimHouseholdKey(binding);
      } catch (error) {
        if (error instanceof HouseholdSharingApiError && error.status === 403) {
          return { state: 'revoked' };
        }
        // L’appareil reste local et peut réessayer après l’autorisation du propriétaire.
      }
    }
    if (binding.householdKey == null) return { state: 'awaiting_key' };

    const currentDigest = snapshot.household == null ? null : await snapshotDigest(snapshot);
    let after = preferRemote ? 0 : binding.checkpoint;
    let checkpoint = binding.checkpoint;
    const operations: Array<Record<string, unknown>> = [];
    for (let page = 0; page < MAX_JOURNAL_PAGES; page += 1) {
      let value: unknown;
      try {
        value = await request<unknown>('/journal/list', {
          body: { after, householdId: binding.householdId, streamId: FROZEN_SAVE_HOUSEHOLD_STREAM_ID },
        });
      } catch (error) {
        if (error instanceof HouseholdSharingApiError && error.status === 403) {
          return { state: 'revoked' };
        }
        throw error;
      }
      if (!isRecord(value) || !Array.isArray(value.operations) || !Number.isInteger(value.checkpoint)) {
        throw new Error('HOUSEHOLD_JOURNAL_INVALID');
      }
      operations.push(...value.operations.filter(isRecord));
      checkpoint = Number(value.checkpoint);
      if (value.hasMore !== true) break;
      const latest = operations.at(-1);
      if (latest == null || !Number.isInteger(latest.sequence)) throw new Error('HOUSEHOLD_JOURNAL_INVALID');
      after = Number(latest.sequence);
      if (page === MAX_JOURNAL_PAGES - 1) throw new Error('HOUSEHOLD_JOURNAL_LIMIT');
    }

    const latest = operations.at(-1);
    if (latest != null) {
      if (
        latest.operationKind !== 'snapshot' || typeof latest.operationId !== 'string' ||
        !Number.isInteger(latest.sequence) || !Number.isInteger(latest.keyVersion) ||
        Number(latest.keyVersion) !== binding.keyVersion
      ) throw new Error('HOUSEHOLD_JOURNAL_INVALID');
      const remote = await decryptSnapshot(
        latest.envelope,
        binding.householdKey,
        binding.householdId,
        binding.keyVersion,
        latest.operationId,
      );
      const remoteDigest = await snapshotDigest(remote);
      const localChanged = currentDigest != null && binding.lastSyncedDigest != null &&
        currentDigest !== binding.lastSyncedDigest;
      if (!preferRemote && localChanged && currentDigest !== remoteDigest) {
        return { state: 'conflict', checkpoint };
      }
      return { state: 'remote', checkpoint, snapshot: remote };
    }

    if (snapshot.household == null || currentDigest == null) return { state: 'unchanged', checkpoint };
    if (binding.lastSyncedDigest === currentDigest) return { state: 'unchanged', checkpoint };
    const operationId = `fs_snapshot_${crypto.randomUUID()}`;
    const envelope = await encryptSnapshot(
      binding.householdKey,
      binding.householdId,
      binding.keyVersion,
      operationId,
      snapshot,
    );
    let appended: unknown;
    try {
      appended = await request<unknown>('/journal/append', {
        body: {
          envelope,
          householdId: binding.householdId,
          keyVersion: binding.keyVersion,
          operationId,
          operationKind: 'snapshot',
          streamId: FROZEN_SAVE_HOUSEHOLD_STREAM_ID,
        },
      });
    } catch (error) {
      if (error instanceof HouseholdSharingApiError && error.status === 403) {
        return { state: 'revoked' };
      }
      throw error;
    }
    if (!isRecord(appended) || !Number.isInteger(appended.sequence)) {
      throw new Error('HOUSEHOLD_JOURNAL_APPEND_INVALID');
    }
    const sequence = Number(appended.sequence);
    if (sequence > binding.checkpoint + 1) return { state: 'conflict', checkpoint: sequence };
    await this.saveBinding({
      ...binding,
      checkpoint: sequence,
      lastSyncedDigest: currentDigest,
      updatedAt: new Date().toISOString(),
    });
    return { state: 'published', checkpoint: sequence };
  }

  async confirmRemoteApplied(snapshot: InventorySnapshot, checkpoint: number): Promise<void> {
    const binding = await this.bindingForLocal(snapshot.household?.id ?? null) ?? await this.firstBinding();
    if (binding == null || snapshot.household == null) throw new Error('HOUSEHOLD_BINDING_REQUIRED');
    await this.saveBinding({
      ...binding,
      localHouseholdId: snapshot.household.id,
      checkpoint,
      lastSyncedDigest: await snapshotDigest(snapshot),
      updatedAt: new Date().toISOString(),
    });
  }

  async revokeMember(
    householdId: string,
    productSubjectId: string,
    snapshot: InventorySnapshot,
  ): Promise<void> {
    let binding = await this.requiredBinding(householdId);
    if (binding.role !== 'owner') throw new Error('HOUSEHOLD_OWNER_REQUIRED');
    await request('/member/revoke', { body: { householdId, targetProductSubjectId: productSubjectId } });
    const householdKey = await crypto.subtle.generateKey(
      { length: 256, name: 'AES-GCM' },
      true,
      ['decrypt', 'encrypt'],
    );
    binding = {
      ...binding,
      householdKey,
      keyVersion: binding.keyVersion + 1,
      lastSyncedDigest: null,
      updatedAt: new Date().toISOString(),
    };
    await this.saveBinding(binding);
    await this.authorizeDevices(householdId);
    const result = await this.synchronize(snapshot);
    if (result.state !== 'published') throw new Error('HOUSEHOLD_KEY_ROTATION_FAILED');
  }

  async revokeCurrentDevice(): Promise<void> {
    const device = await this.device();
    await request('/device/revoke', { body: { keyId: device.keyId } });
    const database = await this.databasePromise;
    await database.clear('bindings');
    await request('/session', { method: 'DELETE' });
  }

  private async device(): Promise<DeviceRecord> {
    const database = await this.databasePromise;
    const key = this.activeDeviceKeyId ?? 'current';
    const current = await database.get('device', key);
    if (current != null) return current;
    const legacy = await database.get('device','current');
    if (this.activeDeviceKeyId != null && legacy?.keyId === this.activeDeviceKeyId) {
      const migrated = {...legacy,key};
      await database.put('device',migrated);
      return migrated;
    }
    const created = await generateDevice(this.activeDeviceKeyId ?? undefined);
    await database.put('device', created);
    return created;
  }

  private async registerDevice(device: DeviceRecord): Promise<void> {
    await request('/device/register', {
      body: { keyId: device.keyId, publicJwk: device.publicJwk },
    });
  }

  private async binding(householdId: string): Promise<BindingRecord | null> {
    return (await (await this.databasePromise).get('bindings', householdId)) ?? null;
  }

  private async bindAuthenticatedHousehold(
    status: HouseholdSharingStatus,
  ): Promise<HouseholdSharingStatus> {
    if (status.state !== 'connected') return status;
    const device = await this.device();
    await this.registerDevice(device);
    if (status.binding != null || status.households.length === 0) return this.status(null);
    const household = status.households[0]!;
    await this.saveBinding({
      householdId: household.householdId,
      accountId: status.productSubjectId,
      localHouseholdId: null,
      role: household.role,
      keyVersion: 1,
      householdKey: null,
      checkpoint: 0,
      lastSyncedDigest: null,
      updatedAt: new Date().toISOString(),
    });
    return this.status(null);
  }

  private async bindingForLocal(localHouseholdId: string | null): Promise<BindingRecord | null> {
    if (localHouseholdId == null) return null;
    return (await (await this.databasePromise).getFromIndex(
      'bindings',
      'by-local-household',
      localHouseholdId,
    )) ?? null;
  }

  private async firstBinding(): Promise<BindingRecord | null> {
    if (this.activeAccountId == null) return null;
    return (await (await this.databasePromise).getAll('bindings'))
      .find((binding) => binding.accountId === this.activeAccountId) ?? null;
  }

  private async requiredBinding(householdId: string): Promise<BindingRecord> {
    const value = await this.binding(householdId);
    if (value == null || value.accountId !== this.activeAccountId) throw new Error('HOUSEHOLD_BINDING_REQUIRED');
    return value;
  }

  private async saveBinding(binding: BindingRecord): Promise<void> {
    await (await this.databasePromise).put('bindings', binding);
  }

  private async putEnvelope(
    binding: BindingRecord,
    recipientKeyId: string,
    recipientProductSubjectId: string,
    recipientPublicJwk: JsonWebKey,
  ): Promise<void> {
    if (binding.householdKey == null) throw new Error('HOUSEHOLD_KEY_REQUIRED');
    await request('/key-envelope/put', {
      body: {
        envelope: await wrapHouseholdKey(
          binding.householdKey,
          binding.householdId,
          binding.keyVersion,
          recipientKeyId,
          recipientPublicJwk,
        ),
        householdId: binding.householdId,
        keyVersion: binding.keyVersion,
        recipientKeyId,
        recipientProductSubjectId,
      },
    });
  }

  private async claimHouseholdKey(binding: BindingRecord): Promise<BindingRecord> {
    const device = await this.device();
    const value = await request<unknown>('/key-envelope/current', {
      body: { householdId: binding.householdId, keyVersion: binding.keyVersion },
    });
    if (!isRecord(value) || value.householdId !== binding.householdId ||
        value.keyVersion !== binding.keyVersion || value.recipientKeyId !== device.keyId) {
      throw new Error('HOUSEHOLD_KEY_ENVELOPE_RESPONSE_INVALID');
    }
    const ready = {
      ...binding,
      householdKey: await unwrapHouseholdKey(
        value.envelope,
        device,
        binding.householdId,
        binding.keyVersion,
      ),
      updatedAt: new Date().toISOString(),
    };
    await this.saveBinding(ready);
    return ready;
  }
}

export class UnavailableHouseholdSharing implements HouseholdSharingPort {
  readonly available = false;

  constructor(private readonly reason: 'demo' | 'not-configured' = 'not-configured') {}

  status(): Promise<HouseholdSharingStatus> {
    return Promise.resolve({ state: 'standalone', reason: this.reason });
  }
  signup(): Promise<never> { return Promise.reject(new Error('HOUSEHOLD_NOT_CONFIGURED')); }
  login(): Promise<never> { return Promise.reject(new Error('HOUSEHOLD_NOT_CONFIGURED')); }
  recover(): Promise<never> { return Promise.reject(new Error('HOUSEHOLD_NOT_CONFIGURED')); }
  deleteAccount(): Promise<never> { return Promise.reject(new Error('HOUSEHOLD_NOT_CONFIGURED')); }
  disconnect(): Promise<void> { return Promise.resolve(); }
  create(): Promise<never> { return Promise.reject(new Error('HOUSEHOLD_NOT_CONFIGURED')); }
  join(): Promise<never> { return Promise.reject(new Error('HOUSEHOLD_NOT_CONFIGURED')); }
  invitationLink(): Promise<never> { return Promise.reject(new Error('HOUSEHOLD_NOT_CONFIGURED')); }
  devices(): Promise<readonly SharedHouseholdDevice[]> { return Promise.resolve([]); }
  authorizeDevices(): Promise<number> { return Promise.resolve(0); }
  synchronize(): Promise<HouseholdSyncResult> { return Promise.resolve({ state: 'unavailable' }); }
  confirmRemoteApplied(): Promise<void> { return Promise.resolve(); }
  revokeMember(): Promise<never> { return Promise.reject(new Error('HOUSEHOLD_NOT_CONFIGURED')); }
  revokeCurrentDevice(): Promise<never> { return Promise.reject(new Error('HOUSEHOLD_NOT_CONFIGURED')); }
}
